import { DYNAMIC_FURNITURE_PRESETS } from "../utils/dynamicFurniture";
import { DynamicTableSVG } from "./map/shapes/DynamicFurnitureShape/DynamicFurnitureShape";
import { Button } from "@gettactic/components/src/primitives/button";
import { logError } from "@gettactic/helpers";
import { Upload } from "lucide-react";
import React from "react";
import { FURNITURE_LIST, Furniture } from "../furniture-list";
import { DEFAULT_FURNITURE_FILL_COLOR } from "./map/defines";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { EditorField } from "./ui/EditorField";
import { IconButton } from "./ui/IconButton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { TooltipProvider } from "./ui/tooltip";

interface FurnitureSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (furniture: Furniture) => void;
  /** Optional API-sourced furniture list. Falls back to static FURNITURE_LIST when not provided. */
  furnitureList?: Furniture[];
  /** Upload handler — receives name, SVG content, and category. Hidden when not provided. */
  onUpload?: (name: string, svg: string, category: string) => Promise<void>;
}

export const FurnitureSelectionModal: React.FC<
  FurnitureSelectionModalProps
> = ({ open, onClose, onSelect, furnitureList, onUpload }) => {
  // Merge built-in furniture with API-provided uploads instead of letting
  // the API list replace it. Built-ins first (so their default categories
  // stay where users expect them), then any uploaded items appended at the
  // end. If an upload reuses a built-in id it overrides that entry in place.
  const items = React.useMemo(() => {
    if (!furnitureList || furnitureList.length === 0) return [...DYNAMIC_FURNITURE_PRESETS, ...FURNITURE_LIST];
    const byId = new Map<string, Furniture>();
    for (const f of [...DYNAMIC_FURNITURE_PRESETS, ...FURNITURE_LIST]) byId.set(f.id, f);
    for (const f of furnitureList) byId.set(f.id, f);
    return Array.from(byId.values());
  }, [furnitureList]);
  const [activeTab, setActiveTab] = React.useState("");
  const [svgCache, setSvgCache] = React.useState<Record<string, string>>({});

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadName, setUploadName] = React.useState("");
  const [uploadSvg, setUploadSvg] = React.useState("");
  const [uploadFileName, setUploadFileName] = React.useState("");
  const [isUploading, setIsUploading] = React.useState(false);
  const [showUploadForm, setShowUploadForm] = React.useState(false);

  const furnitureCategories = React.useMemo(() => {
    const categories: Record<string, Furniture[]> = {};
    items.forEach((item) => {
      if (!categories[item.category]) {
        categories[item.category] = [];
      }
      categories[item.category].push(item);
    });
    return categories;
  }, [items]);

  // Set default active tab to first category
  React.useEffect(() => {
    const categories = Object.keys(furnitureCategories);
    if (categories.length > 0 && !categories.includes(activeTab)) {
      setActiveTab(categories[0]);
    }
  }, [furnitureCategories, activeTab]);

  React.useEffect(() => {
    const fetchSvgs = async () => {
      const svgs: Record<string, string> = {};
      await Promise.all(
        items.map(async (furniture) => {
          if (furniture.url) {
            try {
              const response = await fetch(furniture.url);
              svgs[furniture.url] = await response.text();
            } catch (error) {
              logError(`Failed to load SVG: ${furniture.url}`, error);
            }
          }
        })
      );
      setSvgCache(svgs);
    };

    fetchSvgs();
  }, [items]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadFileName(file.name);
    if (!uploadName) {
      setUploadName(file.name.replace(/\.svg$/i, ""));
    }
    const reader = new FileReader();
    reader.onload = () => {
      setUploadSvg(reader.result as string);
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUploadSubmit = async () => {
    if (!uploadName.trim() || !uploadSvg.trim() || !onUpload || !activeTab)
      return;
    setIsUploading(true);
    try {
      await onUpload(uploadName.trim(), uploadSvg.trim(), activeTab);
      setUploadName("");
      setUploadSvg("");
      setUploadFileName("");
      setShowUploadForm(false);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[80vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>Select Furniture</DialogTitle>
            {onUpload && (
              <TooltipProvider delayDuration={150}>
                <IconButton
                  label="Upload custom furniture"
                  isActive={showUploadForm}
                  onClick={() => setShowUploadForm((v) => !v)}
                >
                  <Upload size={16} />
                </IconButton>
              </TooltipProvider>
            )}
          </div>
        </DialogHeader>
        {showUploadForm && onUpload && (
          <div className="flex flex-col gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".svg"
              onChange={handleFileChange}
              className="hidden"
            />
            <div className="flex items-center gap-2">
              <EditorField
                label="Furniture name"
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                placeholder="Furniture name"
                containerClassName="flex-1"
              />
              <Button
                variant="outline"
                className="h-7 shrink-0 rounded-md px-3 text-xs"
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadFileName || "Choose SVG"}
              </Button>
            </div>
            {uploadSvg && (
              <div className="flex h-12 w-12 items-center justify-center rounded border border-neutral-200 bg-white p-1">
                <div
                  className="h-full w-full [&>svg]:h-full [&>svg]:w-full"
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: Preview of user-selected SVG file
                  dangerouslySetInnerHTML={{ __html: uploadSvg }}
                />
              </div>
            )}
            <p className="text-neutral-500 text-xs">
              Will be added to the <strong>{activeTab}</strong> category
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                className="h-7 rounded-md px-3 text-xs"
                onClick={() => {
                  setShowUploadForm(false);
                  setUploadName("");
                  setUploadSvg("");
                  setUploadFileName("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="h-7 rounded-md px-3 text-xs"
                onClick={handleUploadSubmit}
                disabled={
                  !uploadName.trim() || !uploadSvg.trim() || isUploading
                }
              >
                {isUploading ? "Uploading..." : "Upload"}
              </Button>
            </div>
          </div>
        )}
        <Tabs
          defaultValue="booths"
          className="w-full"
          value={activeTab}
          onValueChange={setActiveTab}
        >
          <TabsList
            className="grid w-full"
            style={{
              gridTemplateColumns: `repeat(${Object.keys(furnitureCategories).length}, 1fr)`
            }}
          >
            {Object.keys(furnitureCategories).map((category) => (
              <TabsTrigger
                key={category}
                value={category}
                className="flex items-center gap-2"
              >
                <img
                  src={furnitureCategories[category][0].url}
                  className="h-4 w-4"
                  alt={`${category} icon`}
                />
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </TabsTrigger>
            ))}
          </TabsList>
          {Object.entries(furnitureCategories).map(
            ([category, categoryItems]) => (
              <TabsContent
                key={category}
                value={category}
                className="mt-4 w-full"
              >
                <div className="grid grid-cols-5 gap-4 p-4">
                  {categoryItems.map((furniture) => (
                    <button
                      key={furniture.id}
                      onClick={() => {
                        onSelect(furniture);
                        onClose();
                      }}
                      className="flex flex-col items-center gap-2 rounded-lg p-4 transition-colors hover:bg-neutral-100"
                      style={{ color: DEFAULT_FURNITURE_FILL_COLOR }}
                    >
                      <span className="flex h-32 w-full items-center justify-center">
                        {furniture.dynamicTable ? <DynamicTableSVG layout={furniture.dynamicTable} /> : svgCache[furniture.url] ? (
                          <span
                            className="flex max-h-full max-w-full items-center justify-center"
                            style={{
                              width: "100%",
                              height: "100%"
                            }}
                            // biome-ignore lint/security/noDangerouslySetInnerHtml: Renders trusted furniture SVGs fetched from our own assets
                            dangerouslySetInnerHTML={{
                              __html: svgCache[furniture.url].replace(
                                /<svg/,
                                '<svg style="max-width: 100%; max-height: 100%; width: auto; height: auto;"'
                              )
                            }}
                          />
                        ) : (
                          <span className="h-24 w-24 animate-pulse rounded bg-neutral-200" />
                        )}
                      </span>
                      <span className="text-center text-neutral-600 text-sm">
                        {furniture.name}
                      </span>
                    </button>
                  ))}
                </div>
              </TabsContent>
            )
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
